<script lang="ts">
export interface TemplateShot {
  id: string
  name: string
  src: string
  width: number
  height: number
}
</script>

<script setup lang="ts">
/**
 * A template page shot opened large: just the page with a soft shadow over a
 * blurred backdrop, previous / next at its sides (or the arrow keys) within
 * the row it was opened from, closed with Esc, the cross or a click beside it.
 *
 * Shared by the strip under the chat and the template picker on the result,
 * which call `open(shots, index)` through a template ref.
 */
const { t } = useI18n()

const dialogRef = ref<HTMLDialogElement | null>(null)
const gallery = ref<TemplateShot[]>([])
const openIndex = ref<number | null>(null)
const isOpen = computed(() => openIndex.value !== null)
const current = computed(() => (openIndex.value === null ? null : gallery.value[openIndex.value]))

function open(shots: TemplateShot[], index: number) {
  gallery.value = shots
  openIndex.value = index
  dialogRef.value?.showModal()
}

function close() {
  dialogRef.value?.close()
}

function step(by: number) {
  if (openIndex.value === null) return
  openIndex.value = (openIndex.value + by + gallery.value.length) % gallery.value.length
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'ArrowRight') step(1)
  else if (event.key === 'ArrowLeft') step(-1)
}

watch(isOpen, (open) => {
  if (open) window.addEventListener('keydown', onKeydown)
  else window.removeEventListener('keydown', onKeydown)
})
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))

defineExpose({ open, close })
</script>

<template>
  <!-- A click on the empty stage around the page closes, like the backdrop. -->
  <dialog
    ref="dialogRef"
    class="template-lightbox m-0 h-dvh max-h-none w-screen max-w-none bg-transparent p-0"
    :aria-label="current ? current.name : undefined"
    @close="openIndex = null"
  >
    <!-- Phones: the page takes the width and the arrows sit over its edges; wider screens keep them beside it -->
    <div class="flex h-full w-full items-center justify-center px-4 py-16 sm:px-24 sm:py-6" @click.self="close">
      <NuxtImg
        v-if="current"
        :key="current.src"
        :src="current.src"
        :alt="current.name"
        format="webp"
        quality="85"
        :width="current.width"
        :height="current.height"
        sizes="xs:100vw md:1200px"
        class="max-h-full w-auto max-w-full rounded-md bg-white object-contain shadow-[0_30px_80px_-20px_rgb(14_14_14/0.35),0_8px_24px_-8px_rgb(14_14_14/0.18)]"
      />
    </div>

    <button type="button" class="lightbox-btn left-3 top-1/2 -translate-y-1/2 sm:left-6" :aria-label="t('cvChat.templates.prev')" @click="step(-1)">
      <i class="bi bi-chevron-left" aria-hidden="true" />
    </button>
    <button type="button" class="lightbox-btn right-3 top-1/2 -translate-y-1/2 sm:right-6" :aria-label="t('cvChat.templates.next')" @click="step(1)">
      <i class="bi bi-chevron-right" aria-hidden="true" />
    </button>
    <button type="button" class="lightbox-btn right-3 top-3 sm:right-6 sm:top-6" :aria-label="t('common.close')" @click="close">
      <i class="bi bi-x-lg" aria-hidden="true" />
    </button>
  </dialog>
</template>

<style scoped>
.template-lightbox::backdrop {
  background: rgb(249 249 250 / 0.8);
  backdrop-filter: blur(8px);
}

:global(.dark-mode) .template-lightbox::backdrop {
  background: rgb(3 7 18 / 0.8);
}

.lightbox-btn {
  @apply absolute inline-flex size-11 items-center justify-center rounded-full bg-white text-lg text-ink shadow-lift transition hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:bg-gray-800 dark:text-white;
}

@media (prefers-reduced-motion: no-preference) {
  .template-lightbox[open] {
    animation: lightbox-in 180ms ease-out;
  }
}

@keyframes lightbox-in {
  from {
    opacity: 0;
    transform: scale(0.98);
  }
}
</style>
